import { SentryError } from '@ifixit/sentry';
import { _js } from '@ifixit/localize';
import { Fragment } from 'react';
import { LoadingModal, renderErrorToUser, OkButton, MainText, SubText } from './basicModal';
import { getWorkOrder } from './thdWorkOrder';
import { MinimalDisclosure } from 'Guide/Schematics/Diagram/Logic/minimalDisclosure';
import { THDFetchFunc, THDFetchProps } from './thdApiModal';
import { iFixitHotSpotItem } from '../Generic/iFixitHotSpotItem';

const renderResponse = (data: any, onClose: MinimalDisclosure['onClose']) => {
   const partNotFound = data.status === 202;

   if (partNotFound) {
      return <PartNotFound json={data.json} onClose={onClose} />;
   }

   return <PartFound json={data.json} onClose={onClose} />;
};

const PartNotFound = ({ onClose, json }: { onClose: MinimalDisclosure['onClose']; json: any }) => {
   const message = json.statusMessage || '';
   const url = json.url || '';
   return (
      <Fragment>
         <MainText>{_js('Something went wrong:')}</MainText>
         <SubText>{message}</SubText>
         <OkButton onClose={onClose} />
         <PartRequestFormButton errorLink={url} />
      </Fragment>
   );
};

const PartFound = ({ onClose, json }: { onClose: MinimalDisclosure['onClose']; json: any }) => (
   <Fragment>
      <MainText>{_js('Part Added!')}</MainText>
      <SubText>{json.statusMessage}</SubText>
      <OkButton onClose={onClose} />
   </Fragment>
);

const PartRequestFormButton = ({ errorLink }: { errorLink: string | null }) => {
   if (!errorLink) {
      return null;
   }

   return (
      <a
         tabIndex={0}
         href={errorLink}
         target="_blank"
         rel="noopener noreferrer"
         className="inline-flex items-center justify-center border border-gray-300 rounded-md px-4 py-2 mt-4 mb-2.5 text-sm font-medium text-gray-800 hover:bg-gray-100 no-underline"
      >
         {_js('Parts Request Form')}
      </a>
   );
};

const RDModal = ({ fetchFn, isOpen, onClose }: { fetchFn?: THDFetchFunc } & MinimalDisclosure) => (
   <LoadingModal
      // @ts-expect-error TS(2322) FIXME: Type '({ apiUrl, apiKey, createdUserId, item, }: {... Remove this comment to see the full error message
      fetchFn={fetchFn || fetchOrderPartApiResult}
      // @ts-expect-error TS(2322) FIXME: Type '({ fetchFn, ...props }: { fetchFn?: THDFetch... Remove this comment to see the full error message
      promiseFn={orderPart}
      onLoading={() => _js('Loading...')}
      onError={renderErrorToUser}
      onSuccess={renderResponse}
      isOpen={isOpen}
      onClose={onClose}
   />
);

const orderPart = async ({ fetchFn, ...props }: { fetchFn?: THDFetchFunc } & THDFetchProps) => {
   // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
   const response = await fetchFn(props);
   const badRequest = response.status === 400 || !response.ok;
   if (badRequest) {
      await throwFetchError(response);
   }

   const result = await response.json();
   if (!result) {
      throw new SentryError('No json returned from THD API');
   }

   return {
      status: response.status,
      json: result,
   };
};

const throwFetchError = async (response: Response) => {
   if (response.ok) {
      return;
   }

   const result = await response
      .json()
      .catch((_error: Error) => ({ errorMessage: response.statusText }));

   // 500 Messages, likely validation Message from thd
   const message =
      result.errorMessage ||
      // 400 message, can't find part etc
      result.statusMessage ||
      // 500 Messages, no SMSession cookie provided
      result.message;
   throw new FetchError(message, result);
};

const fetchOrderPartApiResult = ({
   apiUrl,
   apiKey,
   createdUserId,
   item,
}: {
   apiUrl: string;
   apiKey: string;
   createdUserId: string;
   item: iFixitHotSpotItem;
}) => {
   const body = {
      createdUserId,
      workOrderNbr: getWorkOrder(),
      parts: [
         {
            // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
            partNbr: item?.wiki.sku || '',
            // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
            brandName: item?.wiki.brand || '',
         },
      ],
   };

   const headers = {
      Authorization: `Bearer ${apiKey}`,
      Accept: '*/*',
      'Accept-Encoding': 'gzip, deflate',
      'Content-Type': 'application/json',
   };

   const info = {
      method: 'POST',
      body: JSON.stringify(body),
      headers,
   };

   return fetch(apiUrl, info);
};

class FetchError extends Error {
   data: any;
   constructor(message: string, data: any) {
      super(message);
      this.data = data;
   }
}

export default RDModal;
